import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import { UserListResponse, SystemStatistics, UserStatisticsResponse } from '../types';
import { useAuth } from '../context/AuthContext';

const AdminBoard: React.FC = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<UserListResponse[]>([]);
  const [statistics, setStatistics] = useState<SystemStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Action states
  const [updatingUsers, setUpdatingUsers] = useState<Set<number>>(new Set());
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [userStats, setUserStats] = useState<UserStatisticsResponse | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        skip,
        limit,
      };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter !== '') params.is_active = statusFilter === 'active';
      
      const response = await adminApi.getAllUsers(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [skip, limit, search, roleFilter, statusFilter]);

  const loadStatistics = useCallback(async () => {
    try {
      const response = await adminApi.getSystemStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
      loadStatistics();
    }
  }, [isAdmin, loadUsers, loadStatistics]);

  // Reset to first page when filters change
  useEffect(() => {
    setSkip(0);
  }, [search, roleFilter, statusFilter]);

  const handleUpdateRole = async (userId: number, newRole: 'PLAYER' | 'TO' | 'ADMIN') => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
      return;
    }

    try {
      setUpdatingUsers(prev => new Set(prev).add(userId));
      await adminApi.updateUserRole(userId, newRole);
      await loadUsers();
      await loadStatistics();
    } catch (err: any) {
      console.error('Failed to update role:', err);
      alert(err.response?.data?.detail || 'Failed to update user role');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleUpdateStatus = async (userId: number, isActive: boolean) => {
    const action = isActive ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      setUpdatingUsers(prev => new Set(prev).add(userId));
      await adminApi.updateUserStatus(userId, isActive);
      await loadUsers();
      await loadStatistics();
    } catch (err: any) {
      console.error('Failed to update status:', err);
      alert(err.response?.data?.detail || `Failed to ${action} user`);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleViewStats = async (userId: number) => {
    try {
      const response = await adminApi.getUserStatistics(userId);
      setUserStats(response.data);
      setSelectedUser(userId);
    } catch (err: any) {
      console.error('Failed to load user statistics:', err);
      alert(err.response?.data?.detail || 'Failed to load user statistics');
    }
  };

  const getRoleBadge = (role: string) => {
    const roleConfig: Record<string, { color: string; text: string }> = {
      PLAYER: { color: 'bg-blue-100 text-blue-800', text: 'Player' },
      TO: { color: 'bg-purple-100 text-purple-800', text: 'TO' },
      ADMIN: { color: 'bg-red-100 text-red-800', text: 'Admin' },
    };
    const config = roleConfig[role] || { color: 'bg-gray-100 text-gray-800', text: role };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  if (!isAdmin()) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Board</h1>

        {/* Statistics Dashboard */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Total Users</h3>
              <p className="text-3xl font-bold text-gray-900">{statistics.users.total}</p>
              <p className="text-sm text-gray-600 mt-1">{statistics.users.active} active</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">By Role</h3>
              <div className="space-y-1">
                <p className="text-sm"><span className="font-semibold">Players:</span> {statistics.users.by_role.player}</p>
                <p className="text-sm"><span className="font-semibold">TOs:</span> {statistics.users.by_role.to}</p>
                <p className="text-sm"><span className="font-semibold">Admins:</span> {statistics.users.by_role.admin}</p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Tournaments</h3>
              <p className="text-3xl font-bold text-gray-900">{statistics.tournaments.total}</p>
              <p className="text-sm text-gray-600 mt-1">{statistics.tournaments.active} active</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">System</h3>
              <div className="space-y-1">
                <p className="text-sm"><span className="font-semibold">Circuits:</span> {statistics.circuits.total}</p>
                <p className="text-sm"><span className="font-semibold">Rulesets:</span> {statistics.rulesets.total}</p>
                <p className="text-sm"><span className="font-semibold">Decks:</span> {statistics.decks.total}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Username or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                <option value="PLAYER">Player</option>
                <option value="TO">TO</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-600">Loading users...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">{error}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(user.is_active)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {user.role !== 'TO' && (
                              <button
                                onClick={() => handleUpdateRole(user.id, 'TO')}
                                disabled={updatingUsers.has(user.id)}
                                className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                                title="Grant TO access"
                              >
                                Grant TO
                              </button>
                            )}
                            {user.role === 'TO' && (
                              <button
                                onClick={() => handleUpdateRole(user.id, 'PLAYER')}
                                disabled={updatingUsers.has(user.id)}
                                className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                                title="Revoke TO access"
                              >
                                Revoke TO
                              </button>
                            )}
                            {user.is_active ? (
                              <button
                                onClick={() => handleUpdateStatus(user.id, false)}
                                disabled={updatingUsers.has(user.id)}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                title="Deactivate account"
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateStatus(user.id, true)}
                                disabled={updatingUsers.has(user.id)}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                title="Activate account"
                              >
                                Activate
                              </button>
                            )}
                            <button
                              onClick={() => handleViewStats(user.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View statistics"
                            >
                              Stats
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} users
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0 || loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= total || loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* User Statistics Modal */}
        {userStats && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Statistics for {userStats.username}
                  </h2>
                  <button
                    onClick={() => {
                      setUserStats(null);
                      setSelectedUser(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Email: {userStats.email}</p>
                    <p className="text-sm text-gray-600">Role: {userStats.role}</p>
                    <p className="text-sm text-gray-600">
                      Status: {userStats.is_active ? 'Active' : 'Inactive'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Created: {new Date(userStats.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">User Activity</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Decks</p>
                        <p className="text-2xl font-bold">{userStats.statistics.deck_count}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Submissions</p>
                        <p className="text-2xl font-bold">{userStats.statistics.submission_count}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Registrations</p>
                        <p className="text-2xl font-bold">{userStats.statistics.registration_count}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Tournaments Followed</p>
                        <p className="text-2xl font-bold">{userStats.statistics.follow_count}</p>
                      </div>
                      {userStats.role === 'TO' || userStats.role === 'ADMIN' ? (
                        <div className="bg-gray-50 p-3 rounded">
                          <p className="text-sm text-gray-600">Tournaments Organized</p>
                          <p className="text-2xl font-bold">{userStats.statistics.tournaments_organized}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBoard;

